<template>
  <button
    class="
      flex
      w-10
      h-10
      px-1
      justify-center
      items-center
      border-none
      rounded-full
      disabled:cursor-not-allowed
      relative
    "
    :class="isCountdown ? 'bg-vice-yellow' : 'sm:bg-vice-teal-600 hover:bg-vice-teal-700'"
    @click="recordSnapshot"
    :disabled="!isRecordingServiceReady || succeded || isLoading || isCountdown"
  >
    <ButtonCountdown v-if="isCountdown" :countdownValue="countdownValue" />
    <ViceLoader colorClass="text-vice-light-grey" class="relative left-2" v-if="isLoading" />
    <RecCameraIcon
      v-if="!succeded && !isLoading && !isCountdown"
      class="w-8 h-8"
      :isRecording="isRecording"
    />
    <TickIcon v-if="succeded" class="w-8 h-8" />
  </button>
</template>

<script>
import { ref, watch, onMounted, onUnmounted, inject, computed } from 'vue';

import TickIcon from '@/Components/Icons/TickIcon.vue';
import useSessionStore from '@/Stores/useSessionStore';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import RecCameraIcon from '@/Components/Icons/RecCameraIcon.vue';
import { RECORDING_LIMIT_MINUTES } from '@/Utils/vice-constants';
import ButtonCountdown from '@/Components/Buttons/ButtonCountdown.vue';

export default {
  name: 'VideoSnapshotButton',
  props: {
    jitsiId: {
      type: String,
      required: true,
    },
    sessionId: {
      type: Number,
      required: true,
    },
    sessionUniqueId: {
      type: String,
      required: true,
    },
    deviceName: {
      type: String,
      required: true,
    },
    clinicianId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ['is-recording', 'recording-error'],
  components: { ButtonCountdown, RecCameraIcon, TickIcon, ViceLoader },
  setup(props, { emit }) {
    const sessionStore = useSessionStore();
    const recordingService = inject('recordingService');

    if (!recordingService) {
      console.error("RecordingService not provided. Make sure it's injected at the app level.");
    }

    const succeded = ref(false);
    const isLoading = ref(false);
    const isRecording = ref(false);
    const healthCheckInterval = ref(null);
    const recordingError = ref(null);
    const currentRecordingId = ref(null);
    const isRecordingServiceReady = ref(false);
    const autoStopTimeout = ref(null);
    const MAX_RECORDING_MS = RECORDING_LIMIT_MINUTES * 60 * 1000;

    const isCountdown = ref(false);
    const countdownValue = ref(3);
    const countdownTimerRef = ref(null);

    const showTimer = computed(() => isRecording.value && !isLoading.value && !isCountdown.value);

    const startCountdown = (seconds = 3) =>
      new Promise((resolve, reject) => {
        try {
          if (countdownTimerRef.value) {
            clearInterval(countdownTimerRef.value);
            countdownTimerRef.value = null;
          }
          countdownValue.value = seconds;
          isCountdown.value = true;
          countdownTimerRef.value = setInterval(() => {
            if (countdownValue.value <= 1) {
              clearInterval(countdownTimerRef.value);
              countdownTimerRef.value = null;
              isCountdown.value = false;
              resolve(true);
            } else {
              countdownValue.value -= 1;
            }
          }, 1000);
        } catch (e) {
          isCountdown.value = false;
          reject(e);
        }
      });

    // Initialize recording service connection
    const initializeRecordingService = async () => {
      if (!recordingService) {
        console.error('Recording service not available');
        return;
      }

      try {
        // Initialize/Update recording session
        await recordingService.initializeSession(
          props.sessionId,
          props.sessionUniqueId,
          props.clinicianId,
          [props.deviceName],
        );

        // Check recording server status
        const status = await recordingService.getRecordingServerStatus();
        isRecordingServiceReady.value = status.status === 'online';

        if (!isRecordingServiceReady.value) {
          console.warn('Recording server is not available:', status);
        }
      } catch (error) {
        console.error('Failed to initialize recording service:', error);
        recordingError.value = error.message;
        isRecordingServiceReady.value = false;
      }
    };

    const recordSnapshot = async () => {
      if (!recordingService || !isRecordingServiceReady.value) {
        console.error('Recording service not ready');
        emit('recording-error', 'Recording service not available');
        return;
      }

      recordingError.value = null;

      try {
        if (!isRecording.value) {
          // Get the media stream from the session store
          const stream = sessionStore.consultationStreams[props.deviceName];
          if (!stream) {
            isRecording.value = false;
            throw new Error(`No media stream available for device: ${props.deviceName}`);
          }

          // countdown before starting
          await startCountdown(3);

          // show loader only for the actual start call
          isLoading.value = true;
          currentRecordingId.value = await recordingService.startRecording(
            props.jitsiId,
            props.deviceName,
            stream,
          );

          isRecording.value = true;

          // Schedule auto-stop after 15 minutes if user does not stop manually
          if (autoStopTimeout.value) {
            clearTimeout(autoStopTimeout.value);
          }

          autoStopTimeout.value = setTimeout(async () => {
            if (isRecording.value) {
              try {
                await recordSnapshot();
              } catch (e) {
                try {
                  await recordingService.stopRecording(props.jitsiId);
                } catch (_) {
                  // ignore secondary stop error
                }
                isRecording.value = false;
                currentRecordingId.value = null;
                emit('recording-error', 'Recording auto-stopped after 15 minutes');
              }
            }
          }, MAX_RECORDING_MS);
        } else {
          // Stop recording
          isLoading.value = true;
          if (currentRecordingId.value) {
            await recordingService.stopRecording(props.jitsiId);
          }

          isRecording.value = false;
          succeded.value = true;
          currentRecordingId.value = null;

          // Clear auto-stop timer on manual stop
          if (autoStopTimeout.value) {
            clearTimeout(autoStopTimeout.value);
            autoStopTimeout.value = null;
          }

          setTimeout(() => {
            succeded.value = false;
          }, 3000);
        }
      } catch (error) {
        console.error('Recording operation failed:', error);
        recordingError.value = error.message;
        isRecording.value = false;
        currentRecordingId.value = null;

        emit('recording-error', {
          error: error.message,
          deviceName: props.deviceName,
          operation: isRecording.value ? 'start' : 'stop',
        });

        setTimeout(() => {
          recordingError.value = null;
        }, 5000);
      } finally {
        setTimeout(() => {
          isLoading.value = false;
        }, 1000);
      }
    };

    const startHealthCheck = () => {
      healthCheckInterval.value = setInterval(async () => {
        if (recordingService) {
          try {
            const status = await recordingService.getRecordingServerStatus();
            const wasReady = isRecordingServiceReady.value;
            isRecordingServiceReady.value = status.status === 'online';

            // If service became unavailable during recording, stop recording
            if (wasReady && !isRecordingServiceReady.value && isRecording.value) {
              console.warn('Recording service became unavailable, stopping recording');
              try {
                await recordingService.stopRecording(props.jitsiId);
              } catch (e) {
                // ignore stop errors here
              }
              isRecording.value = false;
              currentRecordingId.value = null;
              // Clear auto-stop timer as well
              if (autoStopTimeout.value) {
                clearTimeout(autoStopTimeout.value);
                autoStopTimeout.value = null;
              }
              emit('recording-error', 'Recording service became unavailable');
            }
          } catch (error) {
            isRecordingServiceReady.value = false;
          }
        }
      }, 30000);
    };

    const stopHealthCheck = () => {
      if (healthCheckInterval.value) {
        clearInterval(healthCheckInterval.value);
        healthCheckInterval.value = null;
      }
    };

    const cleanup = async () => {
      if (isRecording.value && recordingService) {
        try {
          await recordingService.stopRecording(props.jitsiId);
        } catch (error) {
          console.error('Failed to cleanup recording on unmount:', error);
        }
      }
      // Clear auto-stop timer on cleanup
      if (autoStopTimeout.value) {
        clearTimeout(autoStopTimeout.value);
        autoStopTimeout.value = null;
      }
      // clear countdown if active
      if (countdownTimerRef.value) {
        clearInterval(countdownTimerRef.value);
        countdownTimerRef.value = null;
      }
      isCountdown.value = false;
      stopHealthCheck();
    };

    onMounted(async () => {
      await initializeRecordingService();
      startHealthCheck();
    });

    onUnmounted(() => {
      cleanup();
    });

    watch(isRecording, (value) => {
      emit('is-recording', value);
    });

    // Watch for stream changes in session store
    watch(
      () => sessionStore.consultationStreams[props.deviceName],
      (newStream, oldStream) => {
        if (oldStream && !newStream && isRecording.value) {
          console.warn(
            `Stream for ${props.deviceName} was removed while recording, stopping recording`,
          );
          if (autoStopTimeout.value) {
            clearTimeout(autoStopTimeout.value);
            autoStopTimeout.value = null;
          }
          recordSnapshot(); // This stops recording
        }
      },
    );

    return {
      succeded,
      isLoading,
      showTimer,
      isRecording,
      isCountdown,
      countdownValue,
      recordingError,
      isRecordingServiceReady,
      recordSnapshot,
      MAX_RECORDING_MS,
    };
  },
};
</script>
